import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthUser, UserRole, UserStatus } from '@/types/auth';
import { normalizeTabs, type InstitutionTabKey } from '@/utils/institutionTabs';
import { normalizePermissions, type AdminPermissions } from '@/utils/adminPermissions';

interface AuthState {
  token: string | null;
  /** 내 계정 id. `PATCH /users/:id/profile` 처럼 자기 자신을 가리켜야 할 때 쓴다 */
  userId: string | null;
  username: string | null;
  email: string | null;
  role: UserRole | null;
  status: UserStatus | null;
  displayName: string | null;
  institutionId: string | null;
  institutionName: string | null;
  /** 배정된 기관에 등록된 담당자 이름. 표기는 personNameOf() 가 고른다 */
  institutionContactName: string | null;
  allowedTabs: InstitutionTabKey[];
  /** 관리자 화면 기능 권한. 판단은 `useAdminAccess()` 로 한다 */
  adminPermissions: AdminPermissions;
  isAuthenticated: boolean;
  setAuth: (token: string, user: AuthUser) => void;
  setUser: (user: AuthUser) => void;
  clearAuth: () => void;
}

/**
 * 토큰에서 role 만 급히 읽어야 할 때 쓰는 보조 함수.
 *
 * 화면 분기의 정본은 로그인 응답의 `user` 객체다. 이 함수는 저장된 토큰만
 * 있고 프로필을 아직 못 받은 순간(새로고침 직후 `/auth/me` 응답 전)에
 * "관리자 화면인지 기관 화면인지"를 잠깐 가르는 용도로만 쓴다.
 */
function parseJwtRole(token: string): UserRole {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload.role === 'admin') return 'admin';
    if (payload.role === 'institution') return 'institution';
    if (payload.role === 'operator') return 'operator';
    return 'viewer';
  } catch {
    return 'viewer';
  }
}

const EMPTY = {
  token: null,
  userId: null,
  username: null,
  email: null,
  role: null,
  status: null,
  displayName: null,
  institutionId: null,
  institutionName: null,
  institutionContactName: null,
  allowedTabs: [] as InstitutionTabKey[],
  adminPermissions: {} as AdminPermissions,
  isAuthenticated: false,
};

const fromUser = (user: AuthUser) => ({
  userId: user.id,
  username: user.username,
  email: user.email,
  role: user.role,
  status: user.status,
  displayName: user.displayName,
  institutionId: user.institutionId,
  institutionName: user.institutionName,
  institutionContactName: user.institutionContactName ?? null,
  allowedTabs: normalizeTabs(user.allowedTabs),
  adminPermissions: normalizePermissions(user.adminPermissions, user.role),
});

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      ...EMPTY,
      setAuth: (token, user) =>
        set({ ...fromUser(user), token, isAuthenticated: true }),
      setUser: (user) => set(fromUser(user)),
      clearAuth: () => set({ ...EMPTY }),
    }),
    {
      name: 'auth-storage',
      version: 2,
      /**
       * v1 은 role 만 저장했다. 저장된 값을 그대로 살리면 기관 계정 여부·탭
       * 권한이 비어 있는 채로 화면이 그려지므로, 토큰만 남기고 프로필은
       * `/auth/me` 로 다시 받게 만든다.
       */
      migrate: (persisted) => {
        const prev = (persisted ?? {}) as Partial<AuthState>;
        return {
          ...EMPTY,
          token: prev.token ?? null,
          username: prev.username ?? null,
          role: prev.token ? parseJwtRole(prev.token) : null,
          isAuthenticated: !!prev.token,
        } as AuthState;
      },
    },
  ),
);

export { parseJwtRole };
